import React, { ReactElement, useEffect, useState } from 'react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { Tag, Button } from '@ver-uds/react';
import { HiringSite, HiringSitesStates, PaginatedHiringSites, State } from 'eve-account-client';
import { Accordion, Icon } from '@ver-uds/uswds-react';
import { SubmitHandler } from 'react-hook-form';
import ErrorConstants from '../../constants/ErrorConstants';
import { useStatesQuery } from '../../services';
import FormVariant from '../Enrollment/FormVariant';
import SubmitAction from '../Enrollment/SubmitAction';
import AddHiringSiteModal from './Add/AddHiringSiteModal';
import BulkUploadAlert from './BulkUpload/BulkUploadAlert';
import BulkUploadModal from './BulkUpload/BulkUploadModal';
import { ValidationResult } from './BulkUpload/HiringSiteValidator';
import { UploadErrorsProps } from './BulkUpload/UploadErrors';
import EditHiringSiteModal from './Edit/EditHiringSiteModal';
import DeleteHiringSiteModal from './Delete/DeleteHiringSiteModal';
import EditAccordion from './GroupAccordion/EditAccordion';
import EnrollmentAccordion from './GroupAccordion/EnrollmentAccordion';
import OpenModalType from './OpenModalType';

export interface HiringSitesFormProps {
  variant: FormVariant;
  onSubmit: (action: SubmitAction, hiringSite: HiringSite[], uploadErrors: HiringSite[]) => void;
  hiringSitesData?: HiringSite[];
  paginatedData?: PaginatedHiringSites;
  errorMessage?: string;
  onAddOrEditModalClose?: () => void;
  UploadErrors: () => ReactElement<UploadErrorsProps>;
}

const HiringSitesForm = ({
  variant,
  hiringSitesData,
  paginatedData,
  errorMessage,
  onSubmit,
  onAddOrEditModalClose = () => undefined,
  UploadErrors,
}: HiringSitesFormProps): JSX.Element => {
  const [activeHiringSite, setActiveHiringSite] = useState<HiringSite | null>(null);
  const [openModal, setOpenModal] = useState<OpenModalType>(OpenModalType.NONE);
  const [enrollmentErrorMessage, setEnrollmentErrorMessage] = useState<string | undefined>();

  const statesQueryResult = useStatesQuery();
  const { isLoading: isLoadingStates } = statesQueryResult;
  const states = statesQueryResult.data || [];
  const totalHiringSites = hiringSitesData?.length || paginatedData?.paginated?.totalElements || 0;

  useEffect(() => {
    // Closing/resetting modals for (Case: Edit)
    if (!errorMessage) {
      setActiveHiringSite(null);
      setOpenModal(OpenModalType.NONE);
    }
  }, [errorMessage]);

  const buildStatesData = (): HiringSitesStates[] => {
    if (paginatedData) {
      return paginatedData.states;
    }
    // Generate States data
    const rawStatesData = hiringSitesData?.map((site) => {
      return { stateCode: site.stateCode };
    });
    const mappedStatesData = rawStatesData?.map((code, i, arr) => {
      const foundDuplicates = arr?.map((c) => c.stateCode === code.stateCode);
      return {
        stateCode: code.stateCode,
        count: foundDuplicates.length,
      } as HiringSitesStates;
    });
    // Remove duplicates
    return (
      mappedStatesData?.filter(
        (value, i, arr) =>
          i === arr.findIndex((state) => state.stateCode === value.stateCode && state.count === value.count),
      ) || []
    );
  };

  const onCloseModal = (action: SubmitAction) => {
    setEnrollmentErrorMessage('');
    setActiveHiringSite(null);
    setOpenModal(OpenModalType.NONE);
    if (action === SubmitAction.ADD || action === SubmitAction.UPDATE) {
      onAddOrEditModalClose();
    }
  };

  const onAddOrEdit = (action: SubmitAction, data: HiringSite) => {
    const exists = hiringSitesData?.find(
      (site) =>
        site.city.trim() === data.city.trim() &&
        site.line1.trim() === data.line1.trim() &&
        ((!site.line2 && !data.line2) || site.line2 === data.line2 || site.line2?.trim() === data.line2?.trim()) &&
        site.stateCode === data.stateCode &&
        site.zipCode === data.zipCode,
    );
    if (exists) {
      setEnrollmentErrorMessage(ErrorConstants.DUPLICATE_HIRING_SITES);
    } else {
      onSubmit(action, [data], []);
      if (variant === FormVariant.ENROLLMENT) {
        onCloseModal(action);
      }
    }
  };

  const onBulkAdd = (action: SubmitAction, rawValidationResult: ValidationResult) => {
    onSubmit(action, rawValidationResult.validSites, rawValidationResult.invalidSites);
    onCloseModal(action);
  };

  const onAdd: SubmitHandler<HiringSite> = (data) => {
    onAddOrEdit(SubmitAction.ADD, data);
  };

  const onUpdate: SubmitHandler<HiringSite> = (data) => {
    onAddOrEdit(SubmitAction.UPDATE, data);
  };

  const onDelete = () => {
    if (activeHiringSite) {
      onSubmit(SubmitAction.DELETE, [activeHiringSite], []);
    }
  };

  const onBulkUploadAdd: SubmitHandler<ValidationResult> = (rawValidationResult) => {
    onBulkAdd(SubmitAction.BULK_UPLOAD, rawValidationResult);
  };

  const isOpen = (openModalType: OpenModalType) => openModal === openModalType;

  return (
    <>
      {isLoadingStates ? (
        <div className="margin-top-5 margin-bottom-5">
          Loading… <img src={LoaderIcon} alt="" className="height-2" />
        </div>
      ) : (
        <>
          <div className="margin-top-4 grid-row">
            <div className="grid-col-12 desktop:grid-col-6">
              <h2 className="display-flex flex-align-center">
                Total Hiring Sites
                <Tag variant="dark" className="margin-left-2" size="large">
                  {totalHiringSites}
                </Tag>
              </h2>
            </div>
            <div className="grid-col-12 desktop:grid-col-6 display-flex flex-justify-end">
              <Button
                data-testid="bulk-upload-button"
                aria-label="Open modal to bulk upload hiring sites"
                variant="secondary"
                onClick={(): void => setOpenModal(OpenModalType.BULK_UPLOAD)}
                size="small"
              >
                Bulk Upload
              </Button>
              <Button
                data-testid="add-site-button"
                aria-label="Open modal to add hiring site"
                onClick={(): void => setOpenModal(OpenModalType.ADD)}
                size="small"
                iconLeft={<Icon name="add" />}
              >
                Add Hiring Site
              </Button>
            </div>
          </div>
          <div className="grid-col-12 desktop:grid-col-9 margin-y-3 ">
            <BulkUploadAlert openBulkUpload={(): void => setOpenModal(OpenModalType.BULK_UPLOAD)} />
          </div>
          <UploadErrors />
          <AddHiringSiteModal
            open={isOpen(OpenModalType.ADD)}
            close={() => onCloseModal(SubmitAction.ADD)}
            hiringSite={activeHiringSite}
            onSubmit={onAdd}
            errorMessage={errorMessage || enrollmentErrorMessage}
            openBulkUpload={() => setOpenModal(OpenModalType.BULK_UPLOAD)}
          />
          <EditHiringSiteModal
            open={isOpen(OpenModalType.EDIT)}
            close={() => onCloseModal(SubmitAction.UPDATE)}
            hiringSite={activeHiringSite}
            onSubmit={onUpdate}
            errorMessage={errorMessage || enrollmentErrorMessage}
          />
          <DeleteHiringSiteModal
            open={isOpen(OpenModalType.DELETE)}
            close={() => onCloseModal(SubmitAction.DELETE)}
            totalHiringSites={totalHiringSites}
            onSubmit={onDelete}
          />
          <BulkUploadModal
            open={isOpen(OpenModalType.BULK_UPLOAD)}
            close={() => onCloseModal(SubmitAction.BULK_UPLOAD)}
            validStates={states}
            existingSites={hiringSitesData || []}
            onSubmit={onBulkUploadAdd}
          />
          <Accordion className="margin-top-3" bordered multiple>
            {buildStatesData()
              .sort(
                ({ stateCode: stateCode1 }: { stateCode: string }, { stateCode: stateCode2 }: { stateCode: string }) =>
                  stateCode1.localeCompare(stateCode2),
              )
              .map(({ stateCode }) => {
                const [foundState] = states.filter((state: State) => state.code === stateCode);
                const foundHiringSites = hiringSitesData?.filter((site) => site.stateCode === foundState.code);

                return variant === FormVariant.EDIT ? (
                  <EditAccordion
                    key={foundState.code}
                    hiringSiteState={foundState}
                    onModalOpen={(hiringSite: HiringSite, modalType: OpenModalType) => {
                      setActiveHiringSite(hiringSite);
                      setOpenModal(modalType);
                    }}
                  />
                ) : (
                  <EnrollmentAccordion
                    key={foundState.code}
                    hiringSiteState={foundState}
                    hiringSitesData={foundHiringSites}
                    onModalOpen={(hiringSite: HiringSite, modalType: OpenModalType) => {
                      setActiveHiringSite(hiringSite);
                      setOpenModal(modalType);
                    }}
                  />
                );
              })}
          </Accordion>
        </>
      )}
      {totalHiringSites > 0 ? (
        ''
      ) : (
        <p className="display-flex flex-justify-center padding-y-5 padding-x-9 bg-base-lightest">No Hiring Sites</p>
      )}
    </>
  );
};

export default HiringSitesForm;
